'use client'

import { PageContainer } from '@/components/page-container'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useSubscriptions } from '@/hooks/queries'
import { useTariffState } from '@/hooks/use-tariff-state'
import {
	AlertCircle,
	ArrowDownRight,
	ArrowLeftRight,
	CheckCircle2,
	Clock,
	CreditCard,
	Plus,
	RefreshCcw,
	Smartphone,
} from 'lucide-react'
import { useRouter } from 'next/navigation'
import ButtonCancel from './_components/button.cancel'
import { CancelTariffChangeButton } from './_components/button.cancel-tariff-change'
import ButtonResume from './_components/button.resume'

interface Product {
	available: boolean
	type: string
	created_at: string
	updated_at: string
	id: number
	name: string
	price: number
	description: string
	benefits: string[]
	tags: string[]
	duration_days: number
}

interface GroupProduct {
	id: number
	name: string
	description: string
	benefits: string[]
	tags: string[]
	available: boolean
	products: Product[]
}

interface Store {
	id: number
	name: string
}

export interface Subscription {
	status: number
	virtual_subscription: boolean
	auto_renew_status: number
	is_active: boolean
	is_in_billing_retry_period: boolean
	start_at: string
	expires_at: string
	id: string
	user_id: number
	group_product: GroupProduct
	store: Store
	subscription_price: number
}

interface PaymentsResponse {
	count: number
	sum: number
	offset: number
	items: Subscription[]
}

export default function Subscriptions() {
	// Общий react-query кэш: при возврате на страницу данные берутся мгновенно,
	// тот же запрос переиспользуется сайдбаром (без дубля).
	const { data, isLoading } = useSubscriptions()
	// Состояние смены тарифа: активная подписка + запланированное понижение.
	const { activeSub, hasScheduledDowngrade, scheduledSub, scheduledProduct } =
		useTariffState()
	// Запланированный переход (status=0) отдельной карточкой не показываем —
	// его выводим заметкой на активной подписке.
	const subscriptions = (data?.items ?? []).filter(
		s => s.id !== scheduledSub?.id
	)

	const router = useRouter()

	const getStatusBadge = (subscription: Subscription) => {
		// Действующая подписка по is_active (статус может быть 3 при запланированном
		// понижении, но это всё ещё активная подписка).
		if (subscription.is_active) {
			return (
				<div>
					{subscription.virtual_subscription ? (
						<Badge className='py-1 px-3 text-sm bg-green-600/20 text-yellow-400 hover:bg-green-600/30'>
							<CheckCircle2 className='w-4 h-4 mr-1' /> Виртуальная
						</Badge>
					) : (
						<Badge className='py-1 px-3 text-sm bg-green-600/20 text-green-400 hover:bg-green-600/30'>
							<CheckCircle2 className='w-4 h-4 mr-1' /> Активна
						</Badge>
					)}
				</div>
			)
		}
		if (subscription.status === 2) {
			return (
				<div>
					<Badge className='py-1 px-3 text-sm bg-green-600/20 text-yellow-400 hover:bg-green-600/30'>
						<CheckCircle2 className='w-4 h-4 mr-1' /> Просрочена
					</Badge>
				</div>
			)
		}
		if (subscription.status === 3) {
			return (
				<div>
					<Badge className='py-1 px-3 text-sm bg-green-600/20 text-yellow-400 hover:bg-green-600/30'>
						<CheckCircle2 className='w-4 h-4 mr-1' /> Отменена
					</Badge>
				</div>
			)
		}
		if (subscription.status === 4) {
			return (
				<div>
					<Badge className='py-1 px-3 text-sm bg-green-600/20 text-yellow-400 hover:bg-green-600/30'>
						<CheckCircle2 className='w-4 h-4 mr-1' /> Отклонена
					</Badge>
				</div>
			)
		}
		if (subscription.status === 5) {
			return (
				<div>
					<Badge className='py-1 px-3 text-sm bg-green-600/20 text-yellow-400 hover:bg-green-600/30'>
						<CheckCircle2 className='w-4 h-4 mr-1' /> Завершена
					</Badge>
				</div>
			)
		}
		if (subscription.status === 0) {
			return (
				<Badge variant='destructive' className='py-1 px-3 text-sm'>
					<AlertCircle className='w-4 h-4 mr-1' /> Неактивна
				</Badge>
			)
		}
	}

	if (isLoading) {
		return (
			<div className='min-h-screen bg-gray-950'>
				<PageContainer className='py-6 lg:py-10'>
					<div className='space-y-4'>
						<Skeleton className='h-12 w-[250px]' />
						<div className='grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3'>
							{[1, 2, 3].map(i => (
								<Skeleton key={i} className='h-[300px] w-full' />
							))}
						</div>
					</div>
				</PageContainer>
			</div>
		)
	}

	return (
		<div className='min-h-screen bg-gray-950'>
			<PageContainer className='py-6 lg:py-10'>
					<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8 animate-in fade-in-0 slide-in-from-top-2 duration-500'>
						<h1 className='text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text'>
							Мои подписки
						</h1>
						<div className='flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto'>
							<Button
								variant='outline'
								onClick={() => router.push('/profile/devices')}
								className='w-full sm:w-auto gap-2'
							>
								<Smartphone size={18} />
								Мои устройства
							</Button>
							<Button
								onClick={() => router.push('/profile/subscriptions/subscribe')}
								className='w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2'
							>
								<Plus size={18} />
								Оформить подписку
							</Button>
						</div>
					</div>

					<div className='grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3'>
						{subscriptions.map((subscription, i) => (
							<Card
								key={subscription.id}
								style={{ animationDelay: `${100 + i * 100}ms` }}
								className='bg-gray-800/50 border-gray-700 backdrop-blur-sm hover:bg-gray-800/70 transition-all duration-300 animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both'
							>
								<CardHeader>
									<div className='flex justify-between items-start gap-2'>
										<CardTitle className='text-xl lg:text-2xl text-gray-100'>
											{subscription.group_product.name}
										</CardTitle>
										{getStatusBadge(subscription)}
									</div>
									<div className='space-y-4'></div>
									{/* <div className='flex items-center gap-2 text-gray-200  '>
										<span className=' text-white bg-clip-text font-semibold text-3xl md:text-3xl lg:text-4xl '>
											{subscription.subscription_price} ₽
										</span>
									</div> */}
									<CardDescription className='text-sm lg:text-base text-gray-300'>
										{subscription.group_product.description}
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className='space-y-4'>
										<div className='flex items-center gap-2 text-gray-200'>
											<CreditCard className='w-5 h-5 text-blue-400 shrink-0' />
											<div>
												<p className='text-sm text-gray-400'>Стоимость</p>
												<p className='text-base lg:text-lg font-medium'>
													{subscription.subscription_price} ₽
												</p>
											</div>
										</div>
										<div className='flex items-center gap-2 text-gray-200'>
											<Clock className='w-5 h-5 text-blue-400 shrink-0' />
											<div>
												<p className='text-sm text-gray-400'>Действует до</p>
												<p className='text-base lg:text-lg font-medium'>
													{new Date(
														subscription.expires_at
													).toLocaleDateString()}
												</p>
											</div>
										</div>

										{subscription.group_product.benefits.length > 0 && (
											<div className='space-y-2'>
												<p className='text-sm text-gray-400'>Преимущества:</p>
												<ul className='list-disc list-inside space-y-1 text-sm lg:text-base text-gray-200'>
													{subscription.group_product.benefits.map(
														(benefit, index) => (
															<li key={index} className='break-words'>
																{benefit}
															</li>
														)
													)}
												</ul>
											</div>
										)}

										<div className='pt-2 space-y-2'>
											{/* Возобновить можно, только если нет действующей подписки.
											    При запланированном переходе текущая остаётся is_active,
											    поэтому activeSub есть и resume скрыт. */}
											{subscription.status === 3 &&
											!subscription.is_active &&
											!activeSub ? (
												<ButtonResume subscription_id={subscription.id} />
											) : undefined}
											{subscription.id === activeSub?.id &&
												!subscription.virtual_subscription &&
												(hasScheduledDowngrade ? (
													// Пока запланирован переход на другой тариф — менять
													// тариф нельзя; показываем запланированный тариф.
													<div className='rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200'>
														<div className='flex items-center gap-2 font-medium'>
															<ArrowDownRight className='w-4 h-4 shrink-0' />
															Запланирована смена тарифа
														</div>
														<p className='mt-1 text-amber-200/80'>
															На «{scheduledProduct?.name || 'новый тариф'}» — с{' '}
															{new Date(
																scheduledSub?.start_at ||
																	subscription.expires_at
															).toLocaleDateString()}
														</p>
														<CancelTariffChangeButton
															subscriptionId={subscription.id}
															className='mt-2 w-full border-amber-500/50 text-amber-200 hover:bg-amber-500/10'
														/>
													</div>
												) : (
													<Button
														variant='outline'
														onClick={() =>
															router.push(
																'/profile/subscriptions/change-tariff'
															)
														}
														className='w-full gap-2 text-sm lg:text-base'
													>
														<ArrowLeftRight className='w-4 h-4 shrink-0' />
														Сменить тариф
													</Button>
												))}
											{/*{subscription.status === 3 ? (*/}
											{/*	<ButtonResume subscription_id={subscription.id} />*/}
											{/*) : (*/}
											{/*	subscription.status !== 4 && (*/}

											{/*		// <ButtonCancel subscription_id={subscription.id} />*/}
											{/*	)*/}
											{/*)}*/}
										</div>

										{/*{subscription.auto_renew_status === 1 && (*/}
										{/*	<Button*/}
										{/*		variant='outline'*/}
										{/*		className='w-full gap-2 text-sm lg:text-base'*/}
										{/*	>*/}
										{/*		<RefreshCcw className='w-4 h-4 shrink-0' />{' '}*/}
										{/*		Автопродление включено*/}
										{/*	</Button>*/}
										{/*)}*/}
									</div>
								</CardContent>
							</Card>
						))}

						{subscriptions.length === 0 && (
							<Card className='col-span-full bg-gray-800/50 border-gray-700 animate-in fade-in-0 slide-in-from-bottom-2 duration-500 fill-mode-both' style={{ animationDelay: '100ms' }}>
								<CardContent className='flex flex-col items-center justify-center py-8 lg:py-12'>
									<p className='text-lg lg:text-xl text-gray-300 mb-4'>
										У вас пока нет активных подписок
									</p>
									<Button
										className='bg-gradient-to-r from-blue-400 to-purple-500 text-base lg:text-lg'
										onClick={() => {
											router.push('/profile/subscriptions/subscribe')
										}}
									>
										Оформить подписку
									</Button>
								</CardContent>
							</Card>
						)}
					</div>
			</PageContainer>
		</div>
	)
}
